// src/hooks/useGPS.js
// GPS hook with permission handling, continuous watching, geofencing,
// and derived nearest-item sorting.
//
// useGPS()       — one-shot locate with caching
// useWatchGPS()  — continuous position stream (for tracking)
// useNearestItems(items) — sorts any lat/lon array by proximity

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCurrentPosition,
  watchPosition,
  haversineKm,
  haversineMiles,
  inferParish,
  inferZone,
} from '@services/geolocation';
import { captureGPSFailure } from '@services/monitoring';

// ── useGPS ────────────────────────────────────────────────────────
// locate()   — trigger a position request
// Position result is cached for 5 minutes; subsequent locate() calls
// return the cached result unless forceRefresh: true.
const CACHE_TTL = 5 * 60_000;
let _cached = null;

export function useGPS() {
  const [lat,      setLat]      = useState(null);
  const [lon,      setLon]      = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [error,    setError]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [cachedAt, setCachedAt] = useState(null);

  // On mount, restore last cached position so UI isn't blank
  useEffect(() => {
    if (_cached && Date.now() - _cached.ts < CACHE_TTL) {
      setLat(_cached.lat);
      setLon(_cached.lon);
      setAccuracy(_cached.accuracy);
      setCachedAt(_cached.ts);
    }
  }, []);

  const locate = useCallback(async ({ forceRefresh = false } = {}) => {
    // Return cached if still fresh
    if (!forceRefresh && _cached && Date.now() - _cached.ts < CACHE_TTL) {
      return { lat: _cached.lat, lon: _cached.lon, accuracy: _cached.accuracy };
    }

    setLoading(true);
    setError(null);

    try {
      const pos = await getCurrentPosition();
      _cached   = { ...pos, ts: Date.now() };
      setLat(pos.lat);
      setLon(pos.lon);
      setAccuracy(pos.accuracy);
      setCachedAt(Date.now());
      return pos;
    } catch (err) {
      setError(err.message);
      captureGPSFailure(0, err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Derived values
  const parish = lat && lon ? inferParish(lat, lon) : null;
  const zone   = lat && lon ? inferZone(lat, lon)   : null;

  const distanceKm    = useCallback((toLat, toLon) => lat && lon ? haversineKm(lat, lon, toLat, toLon) : null, [lat, lon]);
  const distanceMiles = useCallback((toLat, toLon) => lat && lon ? haversineMiles(lat, lon, toLat, toLon) : null, [lat, lon]);

  return {
    lat, lon, accuracy,
    error, loading,
    hasLocation: !!lat,
    locate,
    parish, zone,
    distanceKm,
    distanceMiles,
    cachedAt,
  };
}

// ── useWatchGPS ───────────────────────────────────────────────────
// Continuously watches position. Call stop() to clean up.
// Useful for real-time family check-in and route tracking.
export function useWatchGPS({ enabled = false } = {}) {
  const [position,  setPosition]  = useState(null);
  const [error,     setError]     = useState(null);
  const [watching,  setWatching]  = useState(false);
  const stopRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    setWatching(true);
    setError(null);

    stopRef.current = watchPosition(
      (pos) => setPosition(pos),
      (err) => {
        setError(err.message ?? 'GPS error');
        setWatching(false);
      },
    );

    return () => {
      stopRef.current?.();
      setWatching(false);
    };
  }, [enabled]);

  const stop = useCallback(() => {
    stopRef.current?.();
    setWatching(false);
  }, []);

  return { position, error, watching, stop };
}

// ── useNearestItems ───────────────────────────────────────────────
// Sorts an array of objects with { lat, lon } by distance from the user.
// Also adds a distanceMiles property to each item for display.
// Items without lat/lon are appended at the end.
export function useNearestItems(items = [], { autoLocate = false } = {}) {
  const gps  = useGPS();
  const [sorted, setSorted] = useState(items);

  useEffect(() => {
    if (autoLocate && !gps.hasLocation && !gps.loading) {
      gps.locate().catch(() => {});
    }
  }, [autoLocate]);

  useEffect(() => {
    if (!gps.lat || !gps.lon) {
      setSorted(items);
      return;
    }

    const withDist = items.map((item) => {
      if (!item.lat || !item.lon) return { ...item, distanceMiles: null };
      return {
        ...item,
        distanceMiles: haversineMiles(gps.lat, gps.lon, item.lat, item.lon),
      };
    });

    withDist.sort((a, b) => {
      if (a.distanceMiles === null) return 1;
      if (b.distanceMiles === null) return -1;
      return a.distanceMiles - b.distanceMiles;
    });

    setSorted(withDist);
  }, [items, gps.lat, gps.lon]);

  return {
    sorted,
    gps,
    locating: gps.loading,
    locate:   gps.locate,
  };
}
